/**
 * MobileCollabModalDetails
 *
 * Full-screen mobile replacement for CollabModalDetails.
 * Uses MUI components throughout with mobile-first design patterns.
 * Renders as a full-screen SwipeableDrawer from bottom.
 *
 * Components from MUI: SwipeableDrawer, Tabs, Tab, TextField, Select, MenuItem,
 *   Autocomplete, Switch, Chip, Button, IconButton, Avatar, Typography, Box,
 *   Card, CardContent, List, ListItem, Divider, Fab, Skeleton, Fade
 */
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import moment from "moment";

// MUI Components
import {
  SwipeableDrawer,
  Box,
  Typography,
  Avatar,
  Chip,
  Tabs,
  Tab,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Autocomplete,
  Switch,
  FormControlLabel,
  Button,
  IconButton,
  Card,
  CardContent,
  CardActionArea,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Fade,
  Skeleton,
  AppBar,
  Toolbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";

// MUI Icons
import CloseIcon from "@mui/icons-material/Close";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import SaveIcon from "@mui/icons-material/Save";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DownloadIcon from "@mui/icons-material/Download";
import AddIcon from "@mui/icons-material/Add";
import VisibilityIcon from "@mui/icons-material/Visibility";
import StarIcon from "@mui/icons-material/Star";

// Redux slices
import {
  showCollabModal,
  setCollabModalData,
  getCollabs,
  getAllCollabs,
  getCompanies,
  getSingleCollabDetails,
  editCollab,
  getPostes,
  getCollabContract,
  editCollabContract,
  deleteCollabContract,
  setSingleCollabProfile,
  typeOfContractOptions,
  civiliteOptions,
  cvSources,
  setSingleCollabManagedTeams,
  optionsSortie,
  updateWeeklyReview,
  updateCollabSuiviStatus,
} from "../../../components/collabs/collabsSlice";
import { setIsLoading } from "../../common/commonSlice";
import {
  setEvaluationAddModal,
  setOnEvaluateTeamId,
  setOnEvaluateCollab,
  setOnEvaluateDate,
  setAllowedToEdit,
} from "../../../components/evaluations/evaluationsSlice";
import {
  deleteAsset,
  downloadAsset,
  getCollabAssets,
  uploadAsset,
  setCollabAssets,
} from "../../../assets/assetsSlice";
import { getSubcontractors } from "../../../components/clients/clientsSlice";
import {
  editCollabMission,
  getCollabMissions,
  setCollabMissions,
  deleteCollabMission,
  editCollabProfile,
} from "../../../components/teams/teamsSlice";

// Shared utilities
import Langs, { Localize } from "../../../app/lang/langs";
import {
  getTagClass,
  showErrorToast,
  showSuccessToast,
  checkPermission,
} from "../../../app/utils/helpers";
import { config } from "../../../app/utils/config";

// Shared components (reused as-is from desktop)
import { DynamicButton } from "../../../components/buttons/DynamicButton";
import { SelectInput } from "../../../components/inputs/Select";
import { ConfirmDeleteModal } from "../../../components/popups/ConfirmDeleteModal";
import MobileAddContractModal from "./MobileAddContractModal";
import MobileAddMissionModal from "./MobileAddMissionModal";
import MobileViewContractModal from "./MobileViewContractModal";
import MobileViewMissionModal from "./MobileViewMissionModal";
import { AssetsPannel } from "../../../components/inputs/AssetsPannel";
import MobileFollowupDrawer from "../../contracts/mobile/MobileFollowupDrawer";
import { SilaeLegend } from "../../../components/collabs/SilaeLegend";

// Map PrimeReact Tag severity to MUI Chip color
const severityToColor = {
  success: "success",
  info: "info",
  warning: "warning",
  danger: "error",
  null: "default",
  undefined: "default",
};

// Date helpers for native date inputs
const dateToInput = (date) =>
  date ? moment(date).format("YYYY-MM-DD") : "";
const inputToDate = (str) => (str ? new Date(str) : null);

// ---------------------------------------------------------------------------
// Shared futuristic style tokens
// ---------------------------------------------------------------------------
const glassEasing = "cubic-bezier(0.4, 0, 0.2, 1)";
const entranceEasing = "cubic-bezier(0, 0, 0.2, 1)";
const TRANSITION_SMOOTH = "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)";
const BORDER_RADIUS_INPUT = "12px";
const BORDER_RADIUS_BUTTON = "14px";

const fieldLabelSx = {
  fontWeight: 600,
  mb: 0.75,
  letterSpacing: "-0.01em",
  color: "text.secondary",
  fontSize: "0.82rem",
  textTransform: "uppercase",
};

const glassmorphicTextFieldSx = {
  mb: 3,
  "& .MuiOutlinedInput-root": {
    borderRadius: BORDER_RADIUS_INPUT,
    backgroundColor: "rgba(255,255,255,0.06)",
    backdropFilter: "blur(6px)",
    transition: TRANSITION_SMOOTH,
    "& fieldset": {
      borderColor: "rgba(0,0,0,0.08)",
      transition: TRANSITION_SMOOTH,
    },
    "&:hover fieldset": {
      borderColor: "rgba(0,0,0,0.18)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "rgba(255,144,21,0.5)",
      boxShadow: "0 0 0 3px rgba(255,144,21,0.12)",
    },
    "&.Mui-focused": {
      backgroundColor: "rgba(255,255,255,0.10)",
    },
  },
  "& input": { fontSize: "16px", transition: TRANSITION_SMOOTH },
  "& textarea": { fontSize: "16px" },
  "& input::placeholder, & textarea::placeholder": { opacity: 0.45 },
};

// Glassmorphism surface helper
const glassSurface = (opacity = 0.55) => ({
  backdropFilter: "blur(16px)",
  WebkitBackdropFilter: "blur(16px)",
  backgroundColor: `rgba(255, 255, 255, ${opacity})`,
});

// ---------------------------------------------------------------------------
// Module-level field style constants — stable references, no remount issues
// ---------------------------------------------------------------------------
const fieldSx = {
  ...glassmorphicTextFieldSx,
  mb: 2,
  width: "100%",
};

const labelSx = {
  ...fieldLabelSx,
  display: "block",
};

// Accent applied to labels of Silae-synced fields — same blue as the
// desktop tables' silaeHeader/silaeCell classes
const silaeLabelSx = {
  borderLeft: "3px solid #0288d1",
  backgroundColor: "rgba(2, 136, 209, 0.06)",
  borderRadius: "2px",
  pl: 1,
};

// Stable field-wrapper component — defined at module level to avoid
// remount on every parent render (inner-component anti-pattern fix)
function MobileField({ label, children, silae }) {
  return (
    <Box sx={{ mb: 2.5 }}>
      <Typography sx={{ ...labelSx, ...(silae ? silaeLabelSx : {}) }}>
        {typeof label === "string" ? <Langs str={label} /> : label}
      </Typography>
      {children}
    </Box>
  );
}

// Stable autocomplete wrapper — same reason
function MobileAutocomplete({ label, value, options, onChange, placeholder, disabled }) {
  const selected = (options || []).find((opt) => opt.value === value) || null;
  return (
    <MobileField label={label}>
      <Autocomplete
        value={selected}
        onChange={(_, newValue) => onChange(newValue)}
        options={options || []}
        getOptionLabel={(opt) => opt.label || ""}
        isOptionEqualToValue={(opt, val) => opt.value === val?.value}
        disabled={disabled}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={placeholder || ""}
            size="small"
            sx={fieldSx}
          />
        )}
        fullWidth
        disableClearable={false}
      />
    </MobileField>
  );
}

// Soft colored shadow (uses palette-derived tones, never pure black)
const softShadow = (level = 1) => {
  const shadows = {
    0: "none",
    1: "0 2px 8px rgba(99, 115, 129, 0.08), 0 1px 2px rgba(99, 115, 129, 0.06)",
    2: "0 4px 16px rgba(99, 115, 129, 0.12), 0 2px 4px rgba(99, 115, 129, 0.08)",
    3: "0 8px 32px rgba(99, 115, 129, 0.16), 0 4px 8px rgba(99, 115, 129, 0.1)",
  };
  return shadows[level] || shadows[1];
};

export default function MobileCollabModalDetails(props) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // ---------------------------------------------------------------------------
  // Redux selectors
  // ---------------------------------------------------------------------------
  const userRoleId = useSelector((state) => state.login.userRoleId);
  const collabData = useSelector((state) => state.collabs.collabModal);
  const canEditClientTeams = checkPermission(config.scopes.clientsTeams, config.permissionTypes.edit)
  const canEditCollabs = checkPermission(config.scopes.collabs, config.permissionTypes.edit)
  const selectedTeamsIds = useSelector((state) => state.teams.selectedTeamsIds);
  const isMobile = useSelector((state) => state.settings.isMobile);
  const companies = useSelector((state) => state.collabs.companies);
  const singleCollabDetails = useSelector(
    (state) => state.collabs.singleCollabDetails
  );
  const singleCollabProfile = useSelector(
    (state) => state.collabs.singleCollabProfile
  );
  const singleCollabManagedTeams = useSelector(
    (state) => state.collabs.singleCollabManagedTeams
  );
  const skillsOptions = useSelector((state) => state.collabs.skillsOptions);
  const postes = useSelector((state) => state.collabs.postes);
  const teams = useSelector((state) => state.teams.allTeams);
  const collabAssets = useSelector((state) => state.assets.collabAssets);
  const subcontractors = useSelector((state) => state.clients.subcontractors);
  const allCollabs = useSelector((state) => state.collabs.allCollabsList);
  const collabMissions = useSelector((state) => state.teams.collabMissions);
  const collabContract = useSelector((state) => state.collabs.collabContract);

  // ---------------------------------------------------------------------------
  // Local state
  // ---------------------------------------------------------------------------
  const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
  const [missionToDelete, setMissionToDelete] = useState(null);
  const [contractToDelete, setContractToDelete] = useState(null);
  const [onGoingMission, setOnGoingMission] = useState(null);
  const [ongoingMissions, setOngoingMissions] = useState([]);
  const [pastMissions, setPastMissions] = useState([]);
  const [upcomingMissions, setUpcomingMissions] = useState([]);
  const [actualContract, setActualContract] = useState(null);
  const [ongoingContracts, setOngoingContracts] = useState([]);
  const [pastContracts, setPastContracts] = useState([]);
  const [upcomingContracts, setUpcomingContracts] = useState([]);
  const [showAddMissionModal, setShowAddMissionModal] = useState(false);
  const [showAddContractModal, setShowAddContractModal] = useState(false);
  const [assetToDelete, setAssetToDelete] = useState(null);
  const [profileData, setProfileData] = useState({});
  const [collabDetails, setCollabDetails] = useState({});
  const [selectedContract, setSelectedContract] = useState(null);
  const [selectedMission, setSelectedMission] = useState(null);
  const [selectedFollowup, setSelectedFollowup] = useState(null);
  const [lockedMissionInput, setLockedMissionInput] = useState("lock");
  const [showUnlockConfirm, setShowUnlockConfirm] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  let modifications = [];

  // ---------------------------------------------------------------------------
  // Effects (same as desktop)
  // ---------------------------------------------------------------------------
  useEffect(() => {
    dispatch(getCompanies());
    dispatch(getPostes());
    dispatch(getSubcontractors());
  }, [dispatch]);

  useEffect(() => {
    if (singleCollabDetails) {
      setCollabDetails(singleCollabDetails);
    }
  }, [singleCollabDetails]);

  useEffect(() => {
    if (collabData && collabData.id) {
      dispatch(getSingleCollabDetails(collabData.id));
      dispatch(getCollabAssets({ id_collab: collabData.id }));
      dispatch(getCollabMissions({ id_collab: collabData.id }));
      dispatch(getCollabContract({ id_collab: collabData.id }));
    }
  }, [collabData]);

  useEffect(() => {
    if (singleCollabProfile) {
      setProfileData(singleCollabProfile);
    } else {
      setProfileData({});
    }
  }, [singleCollabProfile]);

  useEffect(() => {
    if (
      collabMissions &&
      collabMissions.ongoingMissions &&
      collabMissions.ongoingMissions.length > 0
    ) {
      setOnGoingMission(collabMissions.ongoingMissions[0]);
      if (collabMissions.ongoingMissions.length > 1) {
        setOngoingMissions(
          collabMissions.ongoingMissions.slice(
            1,
            collabMissions.ongoingMissions.length
          )
        );
      }
    } else {
      setOnGoingMission(null);
      setOngoingMissions(null);
    }
    if (
      collabMissions &&
      collabMissions.pastMissions &&
      collabMissions.pastMissions.length > 0
    ) {
      setPastMissions(collabMissions.pastMissions);
    } else {
      setPastMissions([]);
    }
    if (
      collabMissions &&
      collabMissions.upcomingMissions &&
      collabMissions.upcomingMissions.length > 0
    ) {
      setUpcomingMissions(collabMissions.upcomingMissions);
    } else {
      setUpcomingMissions([]);
    }
  }, [collabMissions]);

  useEffect(() => {
    if (
      collabContract &&
      collabContract.ongoingContracts &&
      collabContract.ongoingContracts.length > 0
    ) {
      setActualContract(collabContract.ongoingContracts[0]);
      if (collabContract.ongoingContracts.length > 1) {
        setOngoingContracts(
          collabContract.ongoingContracts.slice(
            1,
            collabContract.ongoingContracts.length
          )
        );
      }
    } else {
      setActualContract(null);
      setOngoingContracts([]);
    }
    if (
      collabContract &&
      collabContract.pastContracts &&
      collabContract.pastContracts.length > 0
    ) {
      setPastContracts(collabContract.pastContracts);
    } else {
      setPastContracts([]);
    }
    if (
      collabContract &&
      collabContract.upcomingContracts &&
      collabContract.upcomingContracts.length > 0
    ) {
      setUpcomingContracts(collabContract.upcomingContracts);
    } else {
      setUpcomingContracts([]);
    }
  }, [collabContract]);

  // ---------------------------------------------------------------------------
  // Handlers (preserved exactly from desktop)
  // ---------------------------------------------------------------------------
  const handleClose = (wipe) => {
    dispatch(showCollabModal(false));
    setCollabMissions({});
    setOnGoingMission([]);
    setPastMissions([]);
    setUpcomingMissions([]);
    setOngoingMissions([]);
    setActualContract(null);
    setPastContracts([]);
    setUpcomingContracts([]);
    setOngoingContracts([]);
    setSingleCollabManagedTeams([]);
    setLockedMissionInput("lock");
    setActiveTab(0);
    if (wipe) {
      dispatch(setCollabModalData({ show: false }));
    }
  };

  function handleFieldNumberChange(key, value) {
    let params = { id: collabDetails.id };
    params[key] = value || 0;
    setCollabDetails((current) => ({
      ...current,
      ...params,
    }));
  }

  function handleContractFieldChange(key, value) {
    if (key === "sortie_date" && value) {
      value = moment(value).endOf("day").toDate();
    }
    let params = { id: actualContract.id };
    params[key] = value || null;
    setActualContract((current) => ({
      ...current,
      ...params,
    }));
  }

  function handleMissionFieldChange(key, value) {
    let params = { id: collabData.id };
    params[key] = value || null;
    setOnGoingMission((current) => ({
      ...current,
      ...params,
    }));
  }

  function handleFieldChange(key, value) {
    let params = { id: collabDetails.id };
    params[key] = value || null;
    setCollabDetails((current) => ({
      ...current,
      ...params,
    }));
  }

  function handleProfileFieldChange(key, value) {
    let params = { id: profileData.id };
    params[key] = value || null;
    setProfileData((current) => ({
      ...current,
      ...params,
    }));
  }

  const handleTeamChange = (selectedOption) => {
    if (selectedOption && selectedOption.value !== onGoingMission.id_team) {
      handleMissionFieldChange("id_team", selectedOption.value);
    }
  };

  const handleMissionPosteChange = (selectedOption) => {
    if (selectedOption && selectedOption.value !== onGoingMission.id_poste) {
      handleMissionFieldChange("id_poste", selectedOption.value);
    }
  };

  // Lock toggle — warn before unlocking (enabling team/position editing)
  const handleLockToggle = () => {
    if (lockedMissionInput === "lock") {
      setShowUnlockConfirm(true);
    } else {
      setLockedMissionInput("lock");
    }
  };

  const confirmUnlock = () => {
    setLockedMissionInput("unlock");
    setShowUnlockConfirm(false);
  };

  const handleCollabCiviliteChange = (selectedOption) => {
    if (selectedOption && selectedOption.value !== collabDetails.title) {
      handleFieldChange("title", selectedOption.value);
    }
  };

  const removeMission = (missionId) => {
    dispatch(
      deleteCollabMission({
        id_membership: missionId,
      })
    ).then((response) => {
      if (
        response &&
        response.payload &&
        response.payload.responseData &&
        response.payload.responseData.status === 1
      ) {
        setPastMissions(
          pastMissions?.filter((m) => m.id_mission !== missionId)
        );
        setUpcomingMissions(
          upcomingMissions?.filter((m) => m.id_mission !== missionId)
        );
        setOngoingMissions(
          ongoingMissions?.filter((m) => m.id_mission !== missionId)
        );
      }
    });
  };

  const removeContract = async (contractId) => {
    let response = await dispatch(
      deleteCollabContract({
        id_contract: contractId,
      })
    ).unwrap();
    if (
      response &&
      response.responseData &&
      response.responseData.status === 1
    ) {
      dispatch(getCollabContract({ id_collab: collabData.id }));
    }
  };

  const confirmDeleteMission = () => {
    if (missionToDelete) {
      removeMission(missionToDelete.id_mission);
      setMissionToDelete(null);
      setShowConfirmDeleteModal(false);
    }
  };

  const confirmDeleteContract = () => {
    if (contractToDelete) {
      removeContract(contractToDelete.id);
      setContractToDelete(null);
      setShowConfirmDeleteModal(false);
    }
  };

  const handleFileUpload = async (file, type = "collabs") => {
    if (!file || !["collabs"].includes(type)) return;
    const filename = file.name;
    const tableName = type;
    try {
      if (type === "collabs") {
        dispatch(setIsLoading(true));
        await dispatch(
          uploadAsset({
            file,
            tableName,
            idParent: collabDetails.id,
            filename,
          })
        ).unwrap();
        dispatch(getCollabAssets({ id_collab: collabDetails.id }));
      }
      dispatch(setIsLoading(false));
    } catch (error) {
      console.error(`Error uploading file for ${type}:`, error);
      dispatch(setIsLoading(false));
    }
  };

  const navToTeam = (rowData) => {
    const encodedLabel = encodeURIComponent(rowData.name);
    navigate(`/clients/${selectedTeamsIds[0]}/1/${encodedLabel}`);
    handleClose();
  };

  function getFormattedDate(date) {
    return date ? moment(date).format("YYYY-MM-DD HH:mm:ss") : null;
  }

  // Detect modifications (same logic as desktop renderSaveBtn)
  function computeModifications() {
    let mods = [];
    if (
      collabContract &&
      collabContract.ongoingContracts &&
      collabContract.ongoingContracts[0] &&
      JSON.stringify(actualContract) !==
        JSON.stringify(collabContract.ongoingContracts[0])
    ) {
      mods.push("contract");
    }
    if (
      collabMissions &&
      collabMissions.ongoingMissions &&
      collabMissions.ongoingMissions[0] &&
      JSON.stringify(onGoingMission) !==
        JSON.stringify(collabMissions.ongoingMissions[0])
    ) {
      mods.push("mission");
    }
    if (JSON.stringify(profileData) !== JSON.stringify(singleCollabProfile)) {
      mods.push("profile");
    }
    if (
      collabDetails &&
      JSON.stringify(collabDetails) !== JSON.stringify(singleCollabDetails)
    ) {
      mods.push("collab");
    }
    return mods;
  }

  modifications = computeModifications();

  const handleSaveBtn = async () => {
    let didSucceed = true;
    dispatch(setIsLoading(true));

    if (!collabData) return;

    if (
      props.tabs?.includes("profil") &&
      JSON.stringify(profileData) !== JSON.stringify(singleCollabProfile)
    ) {
      const responseProfile = await dispatch(
        editCollabProfile({
          id: profileData.id,
          id_collab: collabData.id,
          skills: profileData.skills,
          cv_date: getFormattedDate(profileData.cv_date),
          id_poste: profileData.id_poste || null,
          first_experience_date: getFormattedDate(
            profileData.first_experience_date
          ),
          requested_change: profileData.requested_change,
        })
      ).unwrap();

      const profileError =
        responseProfile && responseProfile["responseError"];
      if (profileError) {
        didSucceed = false;
        showErrorToast(profileError.data.error);
      } else {
        dispatch(setSingleCollabProfile(profileData));
      }
    }

    if (
      props.tabs?.includes("mission") &&
      modifications.includes("mission")
    ) {
      const responseMission = await dispatch(
        editCollabMission({
          id_collab: collabData.id,
          ...onGoingMission,
          start_date: getFormattedDate(onGoingMission.start_date),
          end_date: getFormattedDate(onGoingMission.end_date),
        })
      ).unwrap();

      if (responseMission.responseData?.error === "conflict") {
        didSucceed = false;
        showErrorToast("MISSION_CONFLICT_ERROR");
      }

      const missionError =
        responseMission && responseMission["responseError"];
      if (missionError) {
        didSucceed = false;
        showErrorToast(missionError.data.error);
      } else {
        dispatch(getCollabMissions({ id_collab: collabData.id }));
      }
    }

    if (
      props.tabs?.includes("contrat") &&
      modifications.includes("contract")
    ) {
      const responseContract = await dispatch(
        editCollabContract({
          collab_contract: {
            ...actualContract,
            sortie_date: getFormattedDate(actualContract.sortie_date),
            entree_date: getFormattedDate(actualContract.entree_date),
            date_debut_cp: getFormattedDate(actualContract.date_debut_cp),
            date_fin_cp: getFormattedDate(actualContract.date_fin_cp),
            new_date_debut_cp: getFormattedDate(
              actualContract.new_date_debut_cp
            ),
          },
        })
      ).unwrap();

      dispatch(setIsLoading(false));

      if (responseContract.responseData?.error === "conflict") {
        didSucceed = false;
        return showErrorToast("CONTRACT_CONFLICT_ERROR");
      }
      const contractError =
        responseContract && responseContract["responseError"];
      if (contractError) {
        didSucceed = false;
        return showErrorToast();
      }

      dispatch(getCollabContract({ id_collab: collabData.id }));
    }

    if (
      props.tabs?.includes("donneesPerso") &&
      modifications.includes("collab")
    ) {
      const responseCollab = await dispatch(
        editCollab({
          collab: {
            ...collabDetails,
            date_naissance: getFormattedDate(collabDetails.date_naissance),
            anciennetee_date: getFormattedDate(
              collabDetails.anciennetee_date
            ),
            date_fin_titre_sejour: getFormattedDate(
              collabDetails.date_fin_titre_sejour
            ),
            date_theorique_prime_cooptation: getFormattedDate(
              collabDetails.date_theorique_prime_cooptation
            ),
            date_autorisation_licenciement: getFormattedDate(
              collabDetails.date_autorisation_licenciement
            ),
            date_autorisation_embauche: getFormattedDate(
              collabDetails.date_autorisation_embauche
            ),
            handicap_doc_start_date: getFormattedDate(
              collabDetails.handicap_doc_start_date
            ),
            handicap_doc_end_date: getFormattedDate(
              collabDetails.handicap_doc_end_date
            ),
          },
        })
      ).unwrap();

      const collabError = responseCollab && responseCollab["responseError"];
      if (collabError) {
        didSucceed = false;
        showErrorToast();
      }
    }

    if (props.page === "groups") {
      dispatch(getCollabs({ selectedTeamsIds, isCurrent: true }));
    } else {
      dispatch(getAllCollabs());
      dispatch(getSingleCollabDetails(collabData.id));
    }

    dispatch(setIsLoading(false));

    if (didSucceed) {
      showSuccessToast(Localize("success"));
    }
  };

  const handleSaveFollowup = (comments) => {
    if (!selectedFollowup) return;
    const idCollab = selectedFollowup.id_collab ?? collabData?.id;
    dispatch(
      updateWeeklyReview({
        id: selectedFollowup.id,
        comments,
        id_collab: idCollab,
      })
    ).then(() => {
      dispatch(getCollabMissions({ id_collab: idCollab })).then((response) => {
        const responseData = response.payload?.responseData;
        if (responseData && responseData.followups && responseData.followups[0]) {
          dispatch(updateCollabSuiviStatus(responseData.followups[0]));
        }
      });
      setSelectedFollowup(null);
    });
  };

  function addEvaluationDialog() {
    handleClose();
    if (collabData) {
      dispatch(setOnEvaluateTeamId(collabData.id_team));
      dispatch(setOnEvaluateCollab(collabData.id));
    }
    dispatch(setAllowedToEdit(true));
    dispatch(setOnEvaluateDate(Date.now()));
    dispatch(setEvaluationAddModal(true));
  }

  // ---------------------------------------------------------------------------
  // Shared style constants -- ENHANCED with futuristic glassmorphic aesthetic
  // ---------------------------------------------------------------------------
  // fieldSx and labelSx are now module-level constants (see top of file)

  const sectionTitleSx = {
    fontWeight: 700,
    fontSize: "0.92rem",
    color: "text.primary",
    mt: 3,
    mb: 1.5,
    letterSpacing: "-0.01em",
    position: "relative",
    pl: 1.5,
    "&::before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: "50%",
      transform: "translateY(-50%)",
      width: 3,
      height: "70%",
      borderRadius: 2,
      bgcolor: "warning.main",
    },
  };

  // Glass card surface
  const glassCardSx = {
    borderRadius: "16px",
    boxShadow: softShadow(1),
    border: "1px solid rgba(0,0,0,0.06)",
    bgcolor: "rgba(255,255,255,0.85)",
    backdropFilter: "blur(16px)",
    WebkitBackdropFilter: "blur(16px)",
    transition: TRANSITION_SMOOTH,
    "&:hover": {
      boxShadow: softShadow(2),
      transform: "translateY(-1px)",
    },
    "&:active": {
      transform: "scale(0.97)",
      boxShadow: softShadow(1),
    },
  };

  // Shared button style
  const pillButtonSx = {
    borderRadius: BORDER_RADIUS_BUTTON,
    textTransform: "none",
    minHeight: 52,
    fontWeight: 600,
    letterSpacing: "-0.01em",
    transition: TRANSITION_SMOOTH,
    boxShadow: softShadow(1),
    "&:hover": {
      transform: "scale(1.02)",
      boxShadow: `${softShadow(2)}, 0 0 16px rgba(255,144,21,0.18)`,
    },
    "&:active": {
      transform: "scale(0.98)",
      boxShadow: softShadow(0),
    },
  };

  // Icon button enhanced
  const iconBtnSx = {
    minWidth: 44,
    minHeight: 44,
    borderRadius: "12px",
    transition: TRANSITION_SMOOTH,
    "&:hover": {
      backgroundColor: "rgba(99, 115, 129, 0.08)",
      transform: "scale(1.08)",
    },
    "&:active": {
      transform: "scale(0.95)",
    },
  };

  // ---------------------------------------------------------------------------
  // Build available tabs
  // ---------------------------------------------------------------------------
  const availableTabs = [];
  if (props.tabs?.includes("mission")) availableTabs.push({ key: "mission", label: "Mission" });
  if (props.tabs?.includes("contrat")) availableTabs.push({ key: "contrat", label: "Contrat" });
  if (
    props.tabs?.includes("suivis") &&
    collabMissions &&
    collabMissions.followups &&
    collabMissions.followups.length
  ) {
    availableTabs.push({ key: "suivis", label: "Suivis Mission" });
  }
  if (props.tabs?.includes("profil")) availableTabs.push({ key: "profil", label: "Profil" });
  if (props.tabs?.includes("donneesPerso")) availableTabs.push({ key: "donneesPerso", label: "Donn. Perso" });

  // Resolve initial tabIndex from collabData
  useEffect(() => {
    if (collabData && collabData.tabIndex != null && collabData.show) {
      setActiveTab(collabData.tabIndex);
    }
  }, [collabData?.show]);

  // ---------------------------------------------------------------------------
  // Render helpers: Mobile form field
  // ---------------------------------------------------------------------------
  // MobileField and MobileAutocomplete are now module-level components (see top of file)

  const ReadOnlyField = ({ label, value, silae }) => (
    <MobileField label={label} silae={silae}>
      <Typography
        variant="body1"
        sx={{
          fontSize: "0.95rem",
          pl: 1,
          py: 0.5,
          borderRadius: BORDER_RADIUS_INPUT,
          backgroundColor: "rgba(255,255,255,0.06)",
          backdropFilter: "blur(4px)",
          color: "text.primary",
          fontWeight: 400,
          transition: TRANSITION_SMOOTH,
        }}
      >
        {value || "-"}
      </Typography>
    </MobileField>
  );

  const MobileDateField = ({ label, value, onChange, min, disabled, silae }) => (
    <MobileField label={label} silae={silae}>
      <TextField
        type="date"
        value={dateToInput(value)}
        onChange={(e) => onChange(inputToDate(e.target.value))}
        InputLabelProps={{ shrink: true }}
        disabled={disabled}
        inputProps={{
          min: min ? dateToInput(min) : undefined,
          style: { fontSize: "16px" },
        }}
        fullWidth
        size="small"
        sx={fieldSx}
      />
    </MobileField>
  );

  const MobileNumberField = ({ label, value, onChange, disabled }) => (
    <MobileField label={label}>
      <TextField
        type="number"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value ? Number(e.target.value) : 0)}
        inputProps={{ min: 0, max: 999, style: { fontSize: "16px" } }}
        disabled={
          disabled !== undefined
            ? disabled
            : !checkPermission(
                config.scopes.collabs,
                config.permissionTypes.edit
              )
        }
        fullWidth
        size="small"
        sx={fieldSx}
      />
    </MobileField>
  );

  const MobileDropdown = ({ label, value, options, onChange, placeholder }) => (
    <MobileField label={label}>
      <FormControl fullWidth size="small" sx={fieldSx}>
        <Select
          value={value || ""}
          onChange={(e) => onChange(e.target.value || null)}
          displayEmpty
          sx={{
            fontSize: "16px",
            borderRadius: BORDER_RADIUS_INPUT,
            backgroundColor: "rgba(255,255,255,0.06)",
            backdropFilter: "blur(6px)",
            transition: TRANSITION_SMOOTH,
            "&:hover": {
              backgroundColor: "rgba(255,255,255,0.10)",
            },
            "&.Mui-focused": {
              backgroundColor: "rgba(255,255,255,0.10)",
              boxShadow: "0 0 0 3px rgba(255,144,21,0.12)",
            },
          }}
        >
          <MenuItem value="">
            <em>{placeholder || "-"}</em>
          </MenuItem>
          {(options || []).map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </MobileField>
  );

  // ---------------------------------------------------------------------------
  // Mission/Contract history cards -- ENHANCED with glassmorphism
  // ---------------------------------------------------------------------------
  const MissionCard = ({ mission, isPast, onView, onDelete, canDelete = true }) => (
    <Card
      sx={{
        mb: 1.5,
        ...glassCardSx,
        borderLeft: `4px solid ${isPast ? "#db5856" : "#ff9015"}`,
      }}
    >
      <CardActionArea
        onClick={() => onView && onView({ data: mission })}
        sx={{ p: 2 }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: isPast ? "#db5856" : "#ff9015",
                letterSpacing: "-0.01em",
              }}
            >
              {mission.team_name || "-"}
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary", mt: 0.25, display: "block" }}>
              {mission.poste_name || "-"}
            </Typography>
            <Typography variant="caption" display="block" sx={{ color: "text.secondary", mt: 0.25, opacity: 0.7 }}>
              {mission.start_date
                ? new Date(mission.start_date).toLocaleDateString("fr-FR")
                : "-"}{" "}
              {" -> "}{" "}
              {mission.end_date
                ? new Date(mission.end_date).toLocaleDateString("fr-FR")
                : "-"}
            </Typography>
          </Box>
          {canDelete && (
            <IconButton
              size="small"
              color="error"
              onClick={(e) => {
                e.stopPropagation();
                onDelete && onDelete(mission);
              }}
              sx={iconBtnSx}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      </CardActionArea>
    </Card>
  );

  const ContractCard = ({ contract, isPast, isUpcoming, onView, onDelete }) => {
    const color = isUpcoming ? "#ff9015" : isPast ? "#db5856" : "#333";
    return (
      <Card
        sx={{
          mb: 1.5,
          ...glassCardSx,
          borderLeft: `4px solid ${color}`,
        }}
      >
        <CardActionArea
          onClick={() => onView && onView({ data: contract })}
          sx={{ p: 2 }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color, letterSpacing: "-0.01em" }}
              >
                {contract.type_contrat || "-"}
              </Typography>
              <Typography variant="caption" display="block" sx={{ color: "text.secondary", mt: 0.25, opacity: 0.7 }}>
                {contract.entree_date
                  ? new Date(contract.entree_date).toLocaleDateString("fr-FR")
                  : "-"}{" "}
                {" -> "}{" "}
                {contract.sortie_date
                  ? new Date(contract.sortie_date).toLocaleDateString("fr-FR")
                  : "-"}
              </Typography>
            </Box>
            <IconButton
              size="small"
              color="error"
              onClick={(e) => {
                e.stopPropagation();
                onDelete && onDelete(contract);
              }}
              sx={iconBtnSx}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Box>
        </CardActionArea>
      </Card>
    );
  };

  // ---------------------------------------------------------------------------
  // Tab content: Mission
  // ---------------------------------------------------------------------------
  const renderMissionTab = () => {
    if (!onGoingMission) {
      const historyMissions = [
        ...(Array.isArray(pastMissions) ? pastMissions : []),
        ...(Array.isArray(ongoingMissions) ? ongoingMissions : []),
        ...(Array.isArray(upcomingMissions) ? upcomingMissions : []),
      ];
      return (
        <Box sx={{ pb: 2 }}>
          <Box sx={{ textAlign: "center", py: 5 }}>
            <Typography variant="body2" sx={{ mb: 2.5, color: "text.secondary", opacity: 0.8 }}>
              <Langs str="No mission for this collab" />
            </Typography>
            {canEditClientTeams && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setShowAddMissionModal(true)}
                sx={{
                  ...pillButtonSx,
                  background: "linear-gradient(135deg, #FF8701 0%, #FFC100 50%, #FF8701 100%)",
                  backgroundSize: "200% 200%",
                  animation: "gradientSlide 3s ease infinite",
                  "@keyframes gradientSlide": {
                    "0%": { backgroundPosition: "0% 50%" },
                    "50%": { backgroundPosition: "100% 50%" },
                    "100%": { backgroundPosition: "0% 50%" },
                  },
                  color: "#fff",
                  boxShadow: "0 4px 14px rgba(255,144,21,0.35)",
                  "&:hover": {
                    ...pillButtonSx["&:hover"],
                    boxShadow: "0 6px 20px rgba(255,144,21,0.45)",
                    background: "linear-gradient(135deg, #FF8701 0%, #FFC100 50%, #FF8701 100%)",
                    backgroundSize: "200% 200%",
                  },
                  "&:active": {
                    ...pillButtonSx["&:active"],
                    boxShadow: "0 2px 8px rgba(255,144,21,0.25)",
                  },
                }}
              >
                <Langs str="Add mission" />
              </Button>
            )}
          </Box>
          {historyMissions.length > 0 && (
            <>
              <Typography sx={sectionTitleSx}>
                <Langs str="Historique missions" />
              </Typography>
              {historyMissions.map((mission, idx) => (
                <MissionCard
                  key={mission.id_mission || idx}
                  mission={mission}
                  isPast={pastMissions?.includes(mission)}
                  canDelete={canEditClientTeams}
                  onView={(row) => setSelectedMission(row.data)}
                  onDelete={(m) => {
                    setMissionToDelete(m);
                    setShowConfirmDeleteModal(true);
                  }}
                />
              ))}
            </>
          )}
        </Box>
      );
    }

    return (
      <Box sx={{ pb: 2 }}>
        {/* Lock/Unlock toggle for admin */}
        {userRoleId === 1 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              mb: 1.5,
            }}
          >
            <Typography
              variant="caption"
              sx={{ mr: 1, opacity: 0.7, fontWeight: 500 }}
            >
              <Langs str={lockedMissionInput} />
            </Typography>
            <IconButton onClick={handleLockToggle} sx={iconBtnSx}>
              {lockedMissionInput === "lock" ? (
                <LockIcon fontSize="small" />
              ) : (
                <LockOpenIcon fontSize="small" color="warning" />
              )}
            </IconButton>
          </Box>
        )}

        {/* Team */}
        <MobileAutocomplete
          label="Team"
          value={onGoingMission.id_team}
          options={teams || []}
          onChange={(newVal) =>
            handleTeamChange(newVal ? newVal : { value: null })
          }
          placeholder="Team"
          disabled={lockedMissionInput === "lock" || !canEditClientTeams}
        />

        {/* Poste */}
        <MobileAutocomplete
          label="Poste"
          value={onGoingMission.id_poste}
          options={postes || []}
          onChange={(newVal) =>
            handleMissionPosteChange(newVal ? newVal : { value: null })
          }
          placeholder="Poste"
          disabled={lockedMissionInput === "lock" || !canEditClientTeams}
        />

        {/* Start date */}
        <MobileDateField
          label="Date de début"
          value={onGoingMission.start_date}
          onChange={(val) => handleMissionFieldChange("start_date", val)}
          disabled={!canEditClientTeams}
        />

        {/* End date */}
        <MobileDateField
          label="Date de fin"
          value={onGoingMission.end_date}
          onChange={(val) => handleMissionFieldChange("end_date", val)}
          min={onGoingMission.start_date}
          disabled={!canEditClientTeams}
        />

        {/* Created by */}
        <ReadOnlyField
          label="created_by"
          value={
            onGoingMission.created_by_nom && onGoingMission.created_by_prenom
              ? `${onGoingMission.created_by_nom} ${onGoingMission.created_by_prenom}`
              : "-"
          }
        />

        {/* Updated by */}
        <ReadOnlyField
          label="updated_by"
          value={
            onGoingMission.updated_by_nom && onGoingMission.updated_by_prenom
              ? `${onGoingMission.updated_by_nom} ${onGoingMission.updated_by_prenom}`
              : "-"
          }
        />

        {/* AssetsPannel */}
        <AssetsPannel
          tableName="team_members"
          idParent={onGoingMission?.id_mission}
          assets={onGoingMission?.assets}
          uploadCallBackFunction={() => {}}
          deleteCallBackFunction={() => {}}
          label="Assets"
          parentSetter={setOnGoingMission}
          readOnly={!canEditClientTeams}
        />

        {/* Mission history */}
        <Typography sx={sectionTitleSx}>
          <Langs str="Historique missions" />
        </Typography>
        {canEditClientTeams && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              mb: 1.5,
            }}
          >
            <IconButton
              color="warning"
              onClick={() => setShowAddMissionModal(true)}
              sx={iconBtnSx}
            >
              <AddIcon />
            </IconButton>
          </Box>
        )}
        {[
          ...(Array.isArray(pastMissions) ? pastMissions : []),
          ...(Array.isArray(ongoingMissions) ? ongoingMissions : []),
          ...(Array.isArray(upcomingMissions) ? upcomingMissions : []),
        ].map((mission, idx) => (
          <MissionCard
            key={mission.id_mission || idx}
            mission={mission}
            isPast={pastMissions?.includes(mission)}
            canDelete={canEditClientTeams}
            onView={(row) => setSelectedMission(row.data)}
            onDelete={(m) => {
              setMissionToDelete(m);
              setShowConfirmDeleteModal(true);
            }}
          />
        ))}

        {/* Managed teams */}
        {props.page === "collabs" &&
          singleCollabManagedTeams &&
          singleCollabManagedTeams.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography sx={sectionTitleSx}>
                <Langs str="Managed Teams" />
              </Typography>
              {singleCollabManagedTeams.map((team, idx) => (
                <Card
                  key={team.id || idx}
                  sx={{ mb: 1.5, ...glassCardSx }}
                >
                  <CardActionArea
                    onClick={() => navToTeam(team)}
                    sx={{ p: 2 }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, color: "warning.main", letterSpacing: "-0.01em" }}
                    >
                      {team.name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary", opacity: 0.7 }}>
                      {team.manager_role}
                    </Typography>
                  </CardActionArea>
                </Card>
              ))}
            </Box>
          )}
      </Box>
    );
  };

  // ---------------------------------------------------------------------------
  // Tab content: Contrat
  // ---------------------------------------------------------------------------
  const renderContractTab = () => {
    if (!actualContract) {
      const historyContracts = [
        ...(Array.isArray(pastContracts) ? pastContracts : []),
        ...(Array.isArray(ongoingContracts) ? ongoingContracts : []),
        ...(Array.isArray(upcomingContracts) ? upcomingContracts : []),
      ];
      return (
        <Box sx={{ pb: 2 }}>
          <Box sx={{ textAlign: "center", py: 5 }}>
            <Typography variant="body2" sx={{ mb: 2.5, color: "text.secondary", opacity: 0.8 }}>
              <Langs str="No contract for this collab" />
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setShowAddContractModal(true)}
              sx={{
                ...pillButtonSx,
                background: "linear-gradient(135deg, #FF8701 0%, #FFC100 50%, #FF8701 100%)",
                backgroundSize: "200% 200%",
                animation: "gradientSlide 3s ease infinite",
                "@keyframes gradientSlide": {
                  "0%": { backgroundPosition: "0% 50%" },
                  "50%": { backgroundPosition: "100% 50%" },
                  "100%": { backgroundPosition: "0% 50%" },
                },
                color: "#fff",
                boxShadow: "0 4px 14px rgba(255,144,21,0.35)",
                "&:hover": {
                  ...pillButtonSx["&:hover"],
                  boxShadow: "0 6px 20px rgba(255,144,21,0.45)",
                  background: "linear-gradient(135deg, #FF8701 0%, #FFC100 50%, #FF8701 100%)",
                  backgroundSize: "200% 200%",
                },
                "&:active": {
                  ...pillButtonSx["&:active"],
                  boxShadow: "0 2px 8px rgba(255,144,21,0.25)",
                },
              }}
            >
              <Langs str="Add contract" />
            </Button>
          </Box>
          {historyContracts.length > 0 && (
            <>
              <Typography sx={sectionTitleSx}>
                <Langs str="Historique contrats" />
              </Typography>
              {historyContracts.map((contract, idx) => {
                const isPast = pastContracts?.some((c) => c.id === contract.id);
                const isUpcoming = upcomingContracts?.some(
                  (c) => c.id === contract.id
                );
                return (
                  <ContractCard
                    key={contract.id || idx}
                    contract={contract}
                    isPast={isPast}
                    isUpcoming={isUpcoming}
                    onView={(row) => setSelectedContract(row.data)}
                    onDelete={(c) => {
                      setContractToDelete(c);
                      setShowConfirmDeleteModal(true);
                    }}
                  />
                );
              })}
            </>
          )}
        </Box>
      );
    }

    const isExterne = actualContract.type_collab === "externe";

    const renderExterneFields = () => (
      <>
        <MobileDropdown
          label="Société"
          value={actualContract.id_company}
          options={companies || []}
          onChange={(val) => handleContractFieldChange("id_company", val)}
          placeholder="Sélectionnez une société"
        />

        <MobileAutocomplete
          label="Sous traitants"
          value={actualContract.id_subcontractor}
          options={(subcontractors || []).map((sub) => ({
            label: sub.name,
            value: sub.id,
          }))}
          onChange={(newVal) =>
            handleContractFieldChange(
              "id_subcontractor",
              newVal ? newVal.value : null
            )
          }
          placeholder="Sélectionnez un Fournisseur"
        />

        <MobileField label="Localisation">
          <TextField
            value={actualContract.localization || ""}
            onChange={(e) =>
              handleContractFieldChange("localization", e.target.value)
            }
            fullWidth
            size="small"
            sx={fieldSx}
          />
        </MobileField>

        <MobileDropdown
          label="Type de contrat"
          value={actualContract.type_contrat}
          options={typeOfContractOptions}
          onChange={(val) =>
            handleContractFieldChange("type_contrat", val)
          }
          placeholder="Sélectionnez un type de contrat"
        />

        <MobileDateField
          label="Date de début du contrat"
          value={actualContract.entree_date}
          onChange={(val) =>
            handleContractFieldChange("entree_date", val)
          }
        />

        <MobileDateField
          label="Date de fin du contrat"
          value={actualContract.sortie_date}
          onChange={(val) =>
            handleContractFieldChange("sortie_date", val)
          }
          min={actualContract.entree_date}
        />

        <MobileDropdown
          label="Motif de sortie"
          value={actualContract.motif_sortie}
          options={optionsSortie}
          onChange={(val) =>
            handleContractFieldChange("motif_sortie", val)
          }
        />

        <MobileAutocomplete
          label="Responsable contractuel"
          value={actualContract.responsable_contractuel}
          options={allCollabs || []}
          onChange={(newVal) =>
            handleContractFieldChange(
              "responsable_contractuel",
              newVal ? newVal.value : null
            )
          }
          placeholder="Sélectionnez un responsable"
        />

        <MobileField label="N°CP">
          <TextField
            value={actualContract.nb_cp || ""}
            onChange={(e) =>
              handleContractFieldChange("nb_cp", e.target.value)
            }
            fullWidth
            size="small"
            sx={fieldSx}
          />
        </MobileField>

        <MobileDateField
          label="Date début CP"
          value={actualContract.date_debut_cp}
          onChange={(val) =>
            handleContractFieldChange("date_debut_cp", val)
          }
        />

        <MobileDateField
          label="Date fin CP"
          value={actualContract.date_fin_cp}
          onChange={(val) =>
            handleContractFieldChange("date_fin_cp", val)
          }
          min={actualContract.date_debut_cp}
        />

        <MobileDateField
          label="Date fin nouveau CP"
          value={actualContract.new_date_debut_cp}
          onChange={(val) =>
            handleContractFieldChange("new_date_debut_cp", val)
          }
          min={actualContract.date_debut_cp}
        />

        <MobileField label="TJM Achat">
          <TextField
            value={actualContract.tjm_achat || ""}
            onChange={(e) =>
              handleContractFieldChange("tjm_achat", e.target.value)
            }
            fullWidth
            size="small"
            sx={fieldSx}
          />
        </MobileField>

        <MobileField label="TJM Vente">
          <TextField
            value={actualContract.tjm_vente || ""}
            onChange={(e) =>
              handleContractFieldChange("tjm_vente", e.target.value)
            }
            fullWidth
            size="small"
            sx={fieldSx}
          />
        </MobileField>

        <MobileField label="Mutuelle">
          <TextField
            value={actualContract.mutuelle || ""}
            onChange={(e) =>
              handleContractFieldChange("mutuelle", e.target.value)
            }
            fullWidth
            size="small"
            sx={fieldSx}
          />
        </MobileField>

        <MobileField label="Pass Navigo">
          <TextField
            value={actualContract.pass_navigo || ""}
            onChange={(e) =>
              handleContractFieldChange("pass_navigo", e.target.value)
            }
            fullWidth
            size="small"
            sx={fieldSx}
          />
        </MobileField>

        <MobileField label="Tickets Restaurant">
          <TextField
            value={actualContract.ticket_restaurant || ""}
            onChange={(e) =>
              handleContractFieldChange("ticket_restaurant", e.target.value)
            }
            fullWidth
            size="small"
            sx={fieldSx}
          />
        </MobileField>

        <MobileField label="Commentaires">
          <TextField
            value={actualContract.comments || ""}
            onChange={(e) =>
              handleContractFieldChange("comments", e.target.value)
            }
            fullWidth
            multiline
            rows={3}
            size="small"
            sx={fieldSx}
          />
        </MobileField>

        <MobileField label="Did not start">
          <Switch
            checked={actualContract.did_not_start === "yes"}
            onChange={(e) =>
              handleContractFieldChange(
                "did_not_start",
                e.target.checked ? "yes" : "no"
              )
            }
            sx={{
              "& .MuiSwitch-thumb": {
                transition: TRANSITION_SMOOTH,
              },
              "& .MuiSwitch-track": {
                transition: TRANSITION_SMOOTH,
                borderRadius: 99,
              },
            }}
          />
        </MobileField>

        <ReadOnlyField label="created_by" value={actualContract.createdBy} />
      </>
    );

    const renderInterneFields = () => (
      <>
        <SilaeLegend sx={{ mb: 2 }} />

        <MobileField label="Did not start">
          <Switch
            checked={actualContract.did_not_start === "yes"}
            onChange={(e) =>
              handleContractFieldChange(
                "did_not_start",
                e.target.checked ? "yes" : "no"
              )
            }
            sx={{
              "& .MuiSwitch-thumb": {
                transition: TRANSITION_SMOOTH,
              },
              "& .MuiSwitch-track": {
                transition: TRANSITION_SMOOTH,
                borderRadius: 99,
              },
            }}
          />
        </MobileField>

        <MobileField label="Situation">
          <Typography
            sx={{
              fontWeight: 700,
              color:
                ["Présent", "Actif"].includes(collabDetails.statut)
                  ? "#ff9015"
                  : "#db5856",
              py: 0.5,
              pl: 1,
              borderRadius: BORDER_RADIUS_INPUT,
              backgroundColor:
                ["Présent", "Actif"].includes(collabDetails.statut)
                  ? "rgba(255, 144, 21, 0.06)"
                  : "rgba(219, 88, 86, 0.06)",
              display: "inline-block",
              px: 1.5,
            }}
          >
            {collabDetails.statut}
          </Typography>
        </MobileField>

        <ReadOnlyField label="Matricule" value={collabDetails.matricule} silae />
        <ReadOnlyField label="Entité" value={actualContract.id_company} silae />
        <ReadOnlyField label="Type de contrat" value={actualContract.type_contrat} silae />
        <ReadOnlyField label="Statut emploi" value={actualContract.statut_emploi} silae />
        <ReadOnlyField
          label="Etablissement de rattachement"
          value={actualContract.etablissement}
          silae
        />

        <MobileDateField
          label="Date d'ancienneté"
          value={collabDetails.anciennetee_date}
          onChange={(val) => handleFieldChange("anciennetee_date", val)}
          silae
        />

        <MobileDateField
          label="Date de début du contrat"
          value={actualContract.entree_date}
          onChange={(val) =>
            handleContractFieldChange("entree_date", val)
          }
          silae
        />

        <MobileDateField
          label="Date de fin du contrat"
          value={actualContract.sortie_date}
          onChange={(val) =>
            handleContractFieldChange("sortie_date", val)
          }
          min={actualContract.entree_date}
          silae
        />

        <MobileDropdown
          label="Motif de sortie"
          value={actualContract.motif_sortie}
          options={optionsSortie}
          onChange={(val) =>
            handleContractFieldChange("motif_sortie", val)
          }
        />

        <ReadOnlyField label="Nature emploi" value={actualContract.nature_emploi} silae />
        <ReadOnlyField label="Position" value={actualContract.position} silae />
        <ReadOnlyField label="Coefficient" value={actualContract.coefficient} silae />
        <ReadOnlyField label="CodeCSP" value={actualContract.code_csp} silae />
        <ReadOnlyField label="Solde CPN" value={collabDetails.solde_cp_current} />
        <ReadOnlyField label="Solde CPN-1" value={collabDetails.solde_cp_old} />

        <ReadOnlyField
          label="Période d'essai 1"
          value={
            actualContract.periode_essai_1
              ? moment(actualContract.periode_essai_1).format("DD-MM-YYYY")
              : "-"
          }
          silae
        />
        <ReadOnlyField
          label="Durée période essai 1"
          value={actualContract.duree_periode_essai_1}
          silae
        />
        <ReadOnlyField
          label="Période d'essai 2"
          value={
            actualContract.periode_essai_2
              ? moment(actualContract.periode_essai_2).format("DD-MM-YYYY")
              : "-"
          }
          silae
        />
        <ReadOnlyField
          label="Durée période essai 2"
          value={actualContract.duree_periode_essai_2}
          silae
        />

        <MobileDateField
          label="Date d'autorisation d'embauche"
          value={collabDetails.date_autorisation_embauche}
          onChange={(val) =>
            handleFieldChange("date_autorisation_embauche", val)
          }
        />

        <MobileDateField
          label="Date d'autorisation de licenciement"
          value={collabDetails.date_autorisation_licenciement}
          onChange={(val) =>
            handleFieldChange("date_autorisation_licenciement", val)
          }
        />

        <MobileField label="Dedit de formation" silae>
          <TextField
            value={actualContract.dedit_formation || ""}
            onChange={(e) =>
              handleContractFieldChange("dedit_formation", e.target.value)
            }
            inputProps={{ maxLength: 50 }}
            fullWidth
            size="small"
            sx={fieldSx}
          />
        </MobileField>

        <MobileAutocomplete
          label="id_tutor"
          value={actualContract.id_tutor}
          options={
            [...(allCollabs || [])].sort((a, b) =>
              a.label.localeCompare(b.label, undefined, {
                sensitivity: "base",
              })
            )
          }
          onChange={(newVal) =>
            handleContractFieldChange(
              "id_tutor",
              newVal ? newVal.value : null
            )
          }
          placeholder="Tuteur"
        />

        <MobileField label="school">
          <TextField
            value={actualContract.school || ""}
            onChange={(e) =>
              handleContractFieldChange("school", e.target.value)
            }
            inputProps={{ maxLength: 100 }}
            fullWidth
            size="small"
            sx={fieldSx}
          />
        </MobileField>

        <MobileField label="school_contact">
          <TextField
            value={actualContract.school_contact || ""}
            onChange={(e) =>
              handleContractFieldChange("school_contact", e.target.value)
            }
            inputProps={{ maxLength: 100 }}
            fullWidth
            size="small"
            sx={fieldSx}
          />
        </MobileField>

        <MobileField label="diploma">
          <TextField
            value={actualContract.diploma || ""}
            onChange={(e) =>
              handleContractFieldChange("diploma", e.target.value)
            }
            inputProps={{ maxLength: 100 }}
            fullWidth
            size="small"
            sx={fieldSx}
          />
        </MobileField>

        <MobileField label="training_cost">
          <TextField
            value={actualContract.training_cost || ""}
            onChange={(e) =>
              handleContractFieldChange("training_cost", e.target.value)
            }
            inputProps={{ maxLength: 50 }}
            fullWidth
            size="small"
            sx={fieldSx}
          />
        </MobileField>

        <MobileField label="remaining_cost">
          <TextField
            value={actualContract.remaining_cost || ""}
            onChange={(e) =>
              handleContractFieldChange("remaining_cost", e.target.value)
            }
            inputProps={{ maxLength: 50 }}
            fullWidth
            size="small"
            sx={fieldSx}
          />
        </MobileField>

        <MobileField label="Commentaires">
          <TextField
            value={actualContract.comments || ""}
            onChange={(e) =>
              handleContractFieldChange("comments", e.target.value)
            }
            fullWidth
            multiline
            rows={3}
            size="small"
            sx={fieldSx}
          />
        </MobileField>

        <MobileField label="created_by">
          <Typography variant="body2">
            {actualContract.createdBy || (
              <Chip
                label="SILAE"
                size="small"
                sx={{
                  background: "linear-gradient(135deg, orange, rgba(255, 165, 0, 0.85))",
                  color: "#fff",
                  fontWeight: 600,
                  borderRadius: "8px",
                  height: 24,
                  boxShadow: "0 2px 8px rgba(255, 165, 0, 0.25)",
                }}
              />
            )}
          </Typography>
        </MobileField>
      </>
    );

    return (
      <Box sx={{ pb: 2 }}>
        {isExterne ? renderExterneFields() : renderInterneFields()}

        {/* Contract history */}
        <Typography sx={sectionTitleSx}>
          <Langs str="Historique contrats" />
        </Typography>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            mb: 1.5,
          }}
        >
          <IconButton
            color="warning"
            onClick={() => setShowAddContractModal(true)}
            sx={iconBtnSx}
          >
            <AddIcon />
          </IconButton>
        </Box>
        {[
          ...(Array.isArray(pastContracts) ? pastContracts : []),
          ...(Array.isArray(ongoingContracts) ? ongoingContracts : []),
          ...(Array.isArray(upcomingContracts) ? upcomingContracts : []),
        ].map((contract, idx) => {
          const isPast = pastContracts?.some((c) => c.id === contract.id);
          const isUpcoming = upcomingContracts?.some(
            (c) => c.id === contract.id
          );
          return (
            <ContractCard
              key={contract.id || idx}
              contract={contract}
              isPast={isPast}
              isUpcoming={isUpcoming}
              onView={(row) => setSelectedContract(row.data)}
              onDelete={(c) => {
                setContractToDelete(c);
                setShowConfirmDeleteModal(true);
              }}
            />
          );
        })}
      </Box>
    );
  };

  // ---------------------------------------------------------------------------
  // Tab content: Suivis Mission
  // ---------------------------------------------------------------------------
  const renderSuivisTab = () => {
    if (
      !collabMissions ||
      !collabMissions.followups ||
      !collabMissions.followups.length
    ) {
      return (
        <Box sx={{ textAlign: "center", py: 5 }}>
          <Typography variant="body2" sx={{ color: "text.secondary", opacity: 0.8 }}>
            <Langs str="Aucune donnée disponible" />
          </Typography>
        </Box>
      );
    }
    return (
      <Box sx={{ pb: 2 }}>
        {collabMissions.followups.map((followup, idx) => {
          const isDone = followup.status === "DONE";
          return (
            <Card
              key={followup.id || idx}
              sx={{
                mb: 1.5,
                ...glassCardSx,
                borderLeft: `4px solid ${isDone ? "#3a9c60" : "#ff9015"}`,
              }}
            >
              <CardActionArea
                onClick={() =>
                  setSelectedFollowup({
                    ...followup,
                    nom: followup.nom ?? collabData?.nom,
                    prenom: followup.prenom ?? collabData?.prenom,
                  })
                }
                sx={{ p: 2 }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, letterSpacing: "-0.01em" }}
                    >
                      <Langs str={followup.type} />
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: "text.secondary", mt: 0.25, display: "block" }}
                    >
                      <Langs str="Semaine" /> {parseInt(followup.week) + 1}
                      {followup.client ? ` · ${followup.client}` : ""}
                    </Typography>
                    {followup.createdBy && (
                      <Typography
                        variant="caption"
                        display="block"
                        sx={{ color: "text.secondary", mt: 0.25, opacity: 0.7 }}
                      >
                        {followup.createdBy}
                      </Typography>
                    )}
                  </Box>
                  <Chip
                    label={<Langs str={followup.status} />}
                    size="small"
                    color={isDone ? "success" : "warning"}
                    sx={{
                      borderRadius: "8px",
                      height: 24,
                      fontWeight: 600,
                      fontSize: "0.72rem",
                      flexShrink: 0,
                    }}
                  />
                </Box>
              </CardActionArea>
            </Card>
          );
        })}
      </Box>
    );
  };

  // ---------------------------------------------------------------------------
  // Tab content: Profil
  // ---------------------------------------------------------------------------
  const renderProfilTab = () => {
    return (
      <Box sx={{ pb: 2 }}>
        <MobileDateField
          label="Date de dernière mise à jour du CV"
          value={profileData.cv_date}
          onChange={(val) => handleProfileFieldChange("cv_date", val)}
        />

        <MobileDateField
          label="Date de 1ère expérience"
          value={profileData.first_experience_date}
          onChange={(val) =>
            handleProfileFieldChange("first_experience_date", val)
          }
        />

        <MobileField label="Compétences">
          <SelectInput
            values={profileData.skills}
            options={skillsOptions}
            onChange={(values) =>
              handleProfileFieldChange(
                "skills",
                values.map((v) => v.value)
              )
            }
            isMulti
            filter
          />
        </MobileField>

        {/* Assets list */}
        <Typography sx={sectionTitleSx}>
          <Langs str="Pièces jointes" />
        </Typography>
        {collabAssets && collabAssets.length > 0 ? (
          <List
            dense
            sx={{
              bgcolor: "rgba(255,255,255,0.85)",
              backdropFilter: "blur(16px)",
              WebkitBackdropFilter: "blur(16px)",
              borderRadius: "16px",
              mb: 1.5,
              boxShadow: softShadow(1),
              border: "1px solid rgba(0,0,0,0.06)",
              overflow: "hidden",
            }}
          >
            {collabAssets.map((asset, idx) => (
              <React.Fragment key={asset.filename || idx}>
                <ListItem
                  sx={{
                    minHeight: 56,
                    pr: 12,
                    transition: TRANSITION_SMOOTH,
                    "&:hover": {
                      backgroundColor: "rgba(99, 115, 129, 0.04)",
                    },
                  }}
                >
                  <ListItemText
                    primary={asset.filename}
                    primaryTypographyProps={{
                      variant: "body2",
                      noWrap: true,
                      sx: { fontWeight: 500 },
                    }}
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      size="small"
                      onClick={() =>
                        dispatch(
                          downloadAsset({
                            tableName: asset.table_name,
                            idParent: asset.id_parent,
                            filename: asset.filename,
                          })
                        )
                      }
                      sx={iconBtnSx}
                    >
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => {
                        setAssetToDelete(asset);
                        setShowConfirmDeleteModal(true);
                      }}
                      sx={iconBtnSx}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
                {idx < collabAssets.length - 1 && (
                  <Divider sx={{ opacity: 0.4 }} />
                )}
              </React.Fragment>
            ))}
          </List>
        ) : (
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              display: "block",
              mb: 1.5,
              opacity: 0.7,
              pl: 0.5,
            }}
          >
            <Langs str="Aucun fichier" />
          </Typography>
        )}

        <Button
          variant="outlined"
          component="label"
          fullWidth
          sx={{
            ...pillButtonSx,
            boxShadow: "none",
            borderColor: "rgba(0,0,0,0.08)",
            "&:hover": {
              ...pillButtonSx["&:hover"],
              borderColor: "#ff9015",
              backgroundColor: "rgba(255,144,21,0.04)",
              boxShadow: "0 0 12px rgba(255,144,21,0.12)",
            },
          }}
        >
          <Langs str="Upload" />
          <input
            type="file"
            hidden
            onChange={(e) => handleFileUpload(e.target.files[0], "collabs")}
          />
        </Button>
      </Box>
    );
  };

  // ---------------------------------------------------------------------------
  // Tab content: Données Personnelles
  // ---------------------------------------------------------------------------
  const renderDonneesPersoTab = () => {
    const isExterne =
      actualContract && actualContract.type_collab === "externe";

    return (
      <Box sx={{ pb: 2 }}>
        {!isExterne && <SilaeLegend sx={{ mb: 2 }} />}

        <MobileField label="Copyright">
          <Switch
            checked={collabDetails.copyright === "yes"}
            onChange={(e) =>
              handleFieldChange(
                "copyright",
                e.target.checked ? "yes" : "no"
              )
            }
            sx={{
              "& .MuiSwitch-thumb": {
                transition: TRANSITION_SMOOTH,
              },
              "& .MuiSwitch-track": {
                transition: TRANSITION_SMOOTH,
                borderRadius: 99,
              },
            }}
          />
        </MobileField>

        <MobileField label="Email Perso Iso">
          <Switch
            checked={collabDetails.accord_email_cse === "yes"}
            onChange={(e) =>
              handleFieldChange(
                "accord_email_cse",
                e.target.checked ? "yes" : "no"
              )
            }
            sx={{
              "& .MuiSwitch-thumb": {
                transition: TRANSITION_SMOOTH,
              },
              "& .MuiSwitch-track": {
                transition: TRANSITION_SMOOTH,
                borderRadius: 99,
              },
            }}
          />
        </MobileField>

        {isExterne ? (
          <MobileField label="Mail Vital">
            <TextField
              value={collabDetails.email || ""}
              onChange={(e) => handleFieldChange("email", e.target.value)}
              fullWidth
              size="small"
              sx={fieldSx}
            />
          </MobileField>
        ) : (
          <ReadOnlyField label="Mail Vital" value={collabDetails.email} silae />
        )}

        <MobileField label="Login Type" silae={!isExterne}>
          <Chip
            label={collabDetails.type_login}
            size="small"
            sx={{
              background:
                collabDetails.type_login === "O365"
                  ? "linear-gradient(135deg, orange, rgba(255, 165, 0, 0.85))"
                  : "linear-gradient(135deg, #ff9015, rgba(255, 144, 21, 0.85))",
              color: "#fff",
              fontWeight: 600,
              borderRadius: "8px",
              height: 24,
              boxShadow:
                collabDetails.type_login === "O365"
                  ? "0 2px 8px rgba(255, 165, 0, 0.25)"
                  : "0 2px 8px rgba(255, 144, 21, 0.25)",
              transition: TRANSITION_SMOOTH,
            }}
          />
        </MobileField>

        <MobileField label="emergency_contact">
          <TextField
            value={collabDetails.emergency_contact || ""}
            onChange={(e) =>
              handleFieldChange("emergency_contact", e.target.value)
            }
            fullWidth
            size="small"
            sx={fieldSx}
          />
        </MobileField>

        {isExterne ? (
          <MobileDropdown
            label="Civilite"
            value={collabDetails.title}
            options={civiliteOptions}
            onChange={(val) =>
              handleCollabCiviliteChange({ value: val })
            }
          />
        ) : (
          <ReadOnlyField label="Civilite" value={collabDetails.title} silae />
        )}

        {isExterne ? (
          <MobileField label="Nom">
            <TextField
              value={collabDetails.nom || ""}
              onChange={(e) => handleFieldChange("nom", e.target.value)}
              fullWidth
              size="small"
              sx={fieldSx}
            />
          </MobileField>
        ) : (
          <ReadOnlyField label="Nom" value={collabDetails.nom} silae />
        )}

        {isExterne ? (
          <MobileField label="Prénom">
            <TextField
              value={collabDetails.prenom || ""}
              onChange={(e) =>
                handleFieldChange("prenom", e.target.value)
              }
              fullWidth
              size="small"
              sx={fieldSx}
            />
          </MobileField>
        ) : (
          <ReadOnlyField label="Prénom" value={collabDetails.prenom} silae />
        )}

        {isExterne ? (
          <MobileDateField
            label="Date de naissance"
            value={collabDetails.date_naissance}
            onChange={(val) => handleFieldChange("date_naissance", val)}
          />
        ) : (
          <ReadOnlyField
            label="Date de naissance"
            value={collabDetails.date_naissance}
            silae
          />
        )}

        <ReadOnlyField
          label="Security Number"
          value={collabDetails.security_number}
          silae={!isExterne}
        />

        <MobileField label="Ville de résidence" silae={!isExterne}>
          <TextField
            value={collabDetails.city || ""}
            onChange={(e) => handleFieldChange("city", e.target.value)}
            fullWidth
            size="small"
            sx={fieldSx}
          />
        </MobileField>

        <MobileField label="Code Postal" silae={!isExterne}>
          <TextField
            value={collabDetails.postal_code || ""}
            onChange={(e) =>
              handleFieldChange("postal_code", e.target.value)
            }
            fullWidth
            size="small"
            sx={fieldSx}
          />
        </MobileField>

        <MobileField label="Rue" silae={!isExterne}>
          <TextField
            value={collabDetails.address || ""}
            onChange={(e) =>
              handleFieldChange("address", e.target.value)
            }
            fullWidth
            size="small"
            sx={fieldSx}
          />
        </MobileField>

        <MobileDateField
          label="Fin Titre Séjour"
          value={collabDetails.date_fin_titre_sejour}
          onChange={(val) =>
            handleFieldChange("date_fin_titre_sejour", val)
          }
          silae={!isExterne}
        />

        {isExterne ? (
          <MobileField label="Téléphone portable">
            <TextField
              value={collabDetails.mobile_number || ""}
              onChange={(e) =>
                handleFieldChange("mobile_number", e.target.value)
              }
              fullWidth
              size="small"
              sx={fieldSx}
            />
          </MobileField>
        ) : (
          <ReadOnlyField
            label="Téléphone portable"
            value={collabDetails.mobile_number}
            silae
          />
        )}

        {isExterne ? (
          <MobileField label="Téléphone fixe">
            <TextField
              value={collabDetails.telephone_number || ""}
              onChange={(e) =>
                handleFieldChange("telephone_number", e.target.value)
              }
              fullWidth
              size="small"
              sx={fieldSx}
            />
          </MobileField>
        ) : (
          <ReadOnlyField
            label="Téléphone fixe"
            value={collabDetails.telephone_number}
            silae
          />
        )}

        {isExterne ? (
          <MobileField label="Mail Perso">
            <TextField
              value={collabDetails.email_perso || ""}
              onChange={(e) =>
                handleFieldChange("email_perso", e.target.value)
              }
              fullWidth
              size="small"
              sx={fieldSx}
            />
          </MobileField>
        ) : (
          <ReadOnlyField
            label="Mail Perso"
            value={collabDetails.email_perso}
            silae
          />
        )}

        <MobileField label="Nationalité" silae={!isExterne}>
          <TextField
            value={collabDetails.nationality || ""}
            onChange={(e) =>
              handleFieldChange("nationality", e.target.value)
            }
            fullWidth
            size="small"
            sx={fieldSx}
          />
        </MobileField>

        <MobileAutocomplete
          label="Coopteur"
          value={collabDetails.coopteur}
          options={allCollabs || []}
          onChange={(newVal) =>
            handleFieldChange("coopteur", newVal ? newVal.value : null)
          }
          placeholder="Coopteur"
        />

        <MobileField label="Prime cooptation versée">
          <Switch
            checked={collabDetails.prime_cooptation_versee === "yes"}
            onChange={(e) =>
              handleFieldChange(
                "prime_cooptation_versee",
                e.target.checked ? "yes" : "no"
              )
            }
            sx={{
              "& .MuiSwitch-thumb": {
                transition: TRANSITION_SMOOTH,
              },
              "& .MuiSwitch-track": {
                transition: TRANSITION_SMOOTH,
                borderRadius: 99,
              },
            }}
          />
        </MobileField>

        <MobileDateField
          label="Date théorique prime de cooptation"
          value={collabDetails.date_theorique_prime_cooptation}
          onChange={(val) =>
            handleFieldChange("date_theorique_prime_cooptation", val)
          }
        />

        <MobileDropdown
          label="Source CV"
          value={collabDetails.cv_source}
          options={cvSources}
          onChange={(val) => handleFieldChange("cv_source", val)}
          placeholder="Sélectionnez une source"
        />

        <MobileDropdown
          label="RQTH"
          value={collabDetails.is_handicap}
          options={[
            { label: "Oui", value: 1 },
            { label: "Non", value: 0 },
          ]}
          onChange={(val) => handleFieldChange("is_handicap", val)}
        />

        <MobileDateField
          label="Date début de attestation RQTH"
          value={collabDetails.handicap_doc_start_date}
          onChange={(val) =>
            handleFieldChange("handicap_doc_start_date", val)
          }
        />

        <MobileDateField
          label="Date fin de attestation RQTH"
          value={collabDetails.handicap_doc_end_date}
          onChange={(val) =>
            handleFieldChange("handicap_doc_end_date", val)
          }
        />

        {/* Additional fields for interne only */}
        {!isExterne && (
          <>
            <ReadOnlyField
              label="Nombre d'enfants"
              value={collabDetails["nb_enfants"]}
              silae
            />
            <ReadOnlyField
              label="Date de naissance(s) enfant(s)"
              value={collabDetails["dates_naissances_enfants"]}
              silae
            />
            <MobileNumberField
              label="Frais téléphonique"
              value={collabDetails.frais_tel}
              onChange={(val) =>
                handleFieldNumberChange("frais_tel", val)
              }
            />
            <MobileNumberField
              label="Frais de transport"
              value={collabDetails.forfait_deplacement}
              onChange={(val) =>
                handleFieldNumberChange("forfait_deplacement", val)
              }
            />
            <MobileNumberField
              label="Prime Objectif"
              value={collabDetails.prime_objectif}
              onChange={(val) =>
                handleFieldNumberChange("prime_objectif", val)
              }
            />
            <MobileNumberField
              label="Prime Qualité"
              value={collabDetails.prime_qualite}
              onChange={(val) =>
                handleFieldNumberChange("prime_qualite", val)
              }
            />
            <MobileNumberField
              label="Prime Mission"
              value={collabDetails.prime_mission}
              onChange={(val) =>
                handleFieldNumberChange("prime_mission", val)
              }
            />
          </>
        )}
      </Box>
    );
  };

  // ---------------------------------------------------------------------------
  // Tab content router
  // ---------------------------------------------------------------------------
  const renderTabContent = () => {
    if (!availableTabs.length) return null;
    const safeIdx = Math.min(activeTab, availableTabs.length - 1);
    const currentTab = availableTabs[safeIdx];
    if (!currentTab) return null;

    switch (currentTab.key) {
      case "mission":
        return renderMissionTab();
      case "contrat":
        return renderContractTab();
      case "suivis":
        return renderSuivisTab();
      case "profil":
        return renderProfilTab();
      case "donneesPerso":
        return renderDonneesPersoTab();
      default:
        return null;
    }
  };

  // ---------------------------------------------------------------------------
  // Footer buttons -- ENHANCED
  // ---------------------------------------------------------------------------
  const renderFooterButtons = () => {
    const buttons = [];

    // Save button (only when modifications detected) — gated like desktop renderSaveBtn (!canEditCollabs)
    if (modifications.length > 0 && canEditCollabs) {
      buttons.push(
        <Button
          key="save"
          variant="contained"
          color="warning"
          startIcon={<SaveIcon />}
          onClick={handleSaveBtn}
          sx={{
            ...pillButtonSx,
            flex: 1,
            mx: 0.5,
            background: "linear-gradient(135deg, #ff9015, rgba(255, 144, 21, 0.9))",
            boxShadow: "0 4px 16px rgba(255, 144, 21, 0.3)",
            "&:hover": {
              ...pillButtonSx["&:hover"],
              background: "linear-gradient(135deg, #e67e00, rgba(230, 126, 0, 0.95))",
              boxShadow: "0 6px 20px rgba(255, 144, 21, 0.4)",
            },
          }}
        >
          <Langs str="Save" />
        </Button>
      );
    }

    return buttons;
  };

  // ---------------------------------------------------------------------------
  // ConfirmDeleteModal
  // ---------------------------------------------------------------------------
  const ConfirmDelete = () => (
    <ConfirmDeleteModal
      show={showConfirmDeleteModal}
      onHide={() => {
        setShowConfirmDeleteModal(false);
        setAssetToDelete(null);
        setMissionToDelete(null);
        setContractToDelete(null);
      }}
      onDelete={() => {
        if (assetToDelete) {
          dispatch(
            deleteAsset({
              tableName: assetToDelete.table_name,
              idParent: assetToDelete.id_parent,
              filename: assetToDelete.filename,
            })
          );
          dispatch(
            setCollabAssets(
              collabAssets.filter(
                (asset) => asset.filename !== assetToDelete.filename
              )
            )
          );
          setAssetToDelete(null);
        } else if (missionToDelete) {
          confirmDeleteMission();
        } else if (contractToDelete) {
          confirmDeleteContract();
        }
        setShowConfirmDeleteModal(false);
      }}
      title="Confirmation"
      message={
        assetToDelete ? (
          <p>
            <Langs str="Are you sure you want to delete the file" />{" "}
            <strong>{assetToDelete.filename}</strong> ?
          </p>
        ) : missionToDelete ? (
          <p>
            <Langs str="Are you sure you want to delete this mission?" />
          </p>
        ) : (
          <p>
            <Langs str="Are you sure you want to delete this contract?" />
          </p>
        )
      }
    />
  );

  // ---------------------------------------------------------------------------
  // Main render -- ENHANCED with glassmorphism and smooth transitions
  // ---------------------------------------------------------------------------
  const footerButtons = renderFooterButtons();
  const hasFooter = footerButtons.length > 0;

  return (
    <>
      <SwipeableDrawer
        anchor="bottom"
        open={!!collabData.show}
        onClose={() => handleClose({ wipe: true })}
        onOpen={() => {}}
        disableSwipeToOpen
        ModalProps={{ keepMounted: false }}
        PaperProps={{
          sx: {
            height: "100vh",
            maxHeight: "100vh",
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            background: "linear-gradient(180deg, rgba(245, 247, 250, 0.97) 0%, rgba(255, 255, 255, 0.99) 100%)",
          },
        }}
        transitionDuration={{ enter: 350, exit: 280 }}
      >
        {/* Sticky header -- glassmorphic */}
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 10,
            bgcolor: "rgba(255,255,255,0.82)",
            backdropFilter: "blur(14px)",
            WebkitBackdropFilter: "blur(14px)",
            borderBottom: "1px solid rgba(0,0,0,0.06)",
            px: 2.5,
            pt: 2,
            pb: 1,
            boxShadow: "0 1px 12px rgba(0,0,0,0.06)",
          }}
        >
          {/* Close button */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              mb: 0.5,
            }}
          >
            <IconButton
              onClick={() => handleClose({ wipe: true })}
              sx={{
                ...iconBtnSx,
                backgroundColor: "rgba(99, 115, 129, 0.06)",
                "&:hover": {
                  backgroundColor: "rgba(219, 88, 86, 0.1)",
                  color: "#db5856",
                  transform: "scale(1.08) rotate(90deg)",
                },
              }}
              aria-label="Close"
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Avatar + info */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1.5 }}>
            <Avatar
              sx={{
                width: 52,
                height: 52,
                bgcolor: "warning.main",
                fontWeight: 700,
                fontSize: "1.05rem",
                boxShadow: "0 4px 14px rgba(255, 144, 21, 0.3)",
                transition: TRANSITION_SMOOTH,
                letterSpacing: "-0.02em",
              }}
            >
              {collabData.nom && collabData.nom.charAt(0).toUpperCase()}
              {collabData.prenom && collabData.prenom.charAt(0).toUpperCase()}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 700,
                    fontSize: "1.15rem",
                    letterSpacing: "-0.02em",
                    color: "text.primary",
                  }}
                  noWrap
                >
                  {collabData.nom} {collabData.prenom}
                </Typography>
                <Chip
                  label={Localize(
                    actualContract ? actualContract.type_collab : "None"
                  )}
                  size="small"
                  color={
                    severityToColor[
                      getTagClass(
                        actualContract ? actualContract.type_collab : "None"
                      )
                    ] || "default"
                  }
                  sx={{
                    borderRadius: "8px",
                    height: 24,
                    fontWeight: 600,
                    fontSize: "0.72rem",
                    boxShadow: softShadow(1),
                  }}
                />
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}>
                <PhoneIcon sx={{ fontSize: 14, color: "#ff9015", opacity: 0.8 }} />
                <Typography variant="caption" noWrap sx={{ fontWeight: 500, opacity: 0.85 }}>
                  {collabData.mobileNumber ||
                    collabData.mobile_number ||
                    "N/A"}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.25 }}>
                <EmailIcon sx={{ fontSize: 14, color: "warning.main", opacity: 0.8 }} />
                <Typography variant="caption" noWrap sx={{ fontWeight: 500, opacity: 0.85 }}>
                  {collabData.email || "N/A"}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Tabs -- enhanced */}
          {availableTabs.length > 0 && (
            <Tabs
              value={Math.min(activeTab, availableTabs.length - 1)}
              onChange={(_, newVal) => setActiveTab(newVal)}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{
                minHeight: 42,
                "& .MuiTabs-indicator": {
                  height: 3,
                  borderRadius: "3px 3px 0 0",
                  background: "linear-gradient(90deg, #ff9015, rgba(255,144,21,0.7))",
                  transition: `all 350ms ${glassEasing}`,
                },
                "& .MuiTab-root": {
                  minHeight: 42,
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.82rem",
                  px: 1.5,
                  letterSpacing: "-0.01em",
                  borderRadius: "10px 10px 0 0",
                  transition: TRANSITION_SMOOTH,
                  "&:hover": {
                    backgroundColor: "rgba(255,144,21,0.06)",
                  },
                  "&.Mui-selected": {
                    fontWeight: 700,
                    color: "#ff9015",
                  },
                },
              }}
            >
              {availableTabs.map((tab) => (
                <Tab key={tab.key} label={tab.label} />
              ))}
            </Tabs>
          )}
        </Box>

        {/* Scrollable body */}
        <Box
          sx={{
            flex: 1,
            overflow: "auto",
            px: 2.5,
            pt: 2.5,
            pb: hasFooter ? "90px" : 2.5,
            WebkitOverflowScrolling: "touch",
            scrollBehavior: "smooth",
            "&::-webkit-scrollbar": {
              width: 6,
            },
            "&::-webkit-scrollbar-track": {
              background: "transparent",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "rgba(99, 115, 129, 0.18)",
              borderRadius: 99,
              transition: TRANSITION_SMOOTH,
              "&:hover": {
                background: "rgba(99, 115, 129, 0.32)",
              },
            },
          }}
        >
          <Fade in timeout={350} easing={{ enter: entranceEasing }}>
            <Box
              sx={{
                animation: "fadeSlideIn 350ms cubic-bezier(0, 0, 0.2, 1)",
                "@keyframes fadeSlideIn": {
                  "0%": {
                    opacity: 0,
                    transform: "translateY(10px)",
                  },
                  "100%": {
                    opacity: 1,
                    transform: "translateY(0)",
                  },
                },
              }}
            >
              {renderTabContent()}
            </Box>
          </Fade>
        </Box>

        {/* Sticky footer -- glassmorphic */}
        {hasFooter && (
          <Box
            sx={{
              position: "sticky",
              bottom: 0,
              bgcolor: "rgba(255,255,255,0.78)",
              backdropFilter: "blur(18px)",
              WebkitBackdropFilter: "blur(18px)",
              borderTop: "1px solid rgba(255,255,255,0.5)",
              px: 2.5,
              py: 2,
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
              zIndex: 10,
              boxShadow: "0 -4px 24px rgba(0,0,0,0.06)",
            }}
          >
            {footerButtons}
          </Box>
        )}
      </SwipeableDrawer>

      {/* Sub-modals (reused from desktop) */}
      <ConfirmDelete />
      <MobileAddMissionModal
        show={showAddMissionModal}
        onHide={() => setShowAddMissionModal(false)}
        collabDetails={collabDetails}
        onGoingMission={onGoingMission}
        collabMissions={collabMissions}
      />
      <MobileAddContractModal
        show={showAddContractModal}
        actualContract={actualContract}
        onHide={() => setShowAddContractModal(false)}
        collabDetails={collabDetails}
        collabContract={collabContract}
      />
      <MobileViewContractModal
        selectedContract={selectedContract}
        onHide={() => setSelectedContract(null)}
      />
      <MobileViewMissionModal
        selectedMission={selectedMission}
        onHide={() => setSelectedMission(null)}
      />
      <MobileFollowupDrawer
        selected={selectedFollowup}
        onHide={() => setSelectedFollowup(null)}
        zIndex={1402}
        editable
        onSave={handleSaveFollowup}
      />

      {/* Confirmation before unlocking team/position editing */}
      <Dialog
        open={showUnlockConfirm}
        onClose={() => setShowUnlockConfirm(false)}
        fullWidth
        maxWidth={false}
        sx={{ zIndex: 1402 }}
        PaperProps={{
          sx: {
            borderRadius: "16px",
            m: 2,
            width: { xs: "calc(100% - 32px)", md: "50%" },
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          <Langs str="MISSION_UNLOCK_WARNING_TITLE" />
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: "text.primary" }}>
            <Langs str="MISSION_UNLOCK_WARNING_MSG" />
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setShowUnlockConfirm(false)}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            <Langs str="NO" />
          </Button>
          <Button
            onClick={confirmUnlock}
            variant="contained"
            color="warning"
            startIcon={<LockOpenIcon />}
            sx={{ textTransform: "none", fontWeight: 600, borderRadius: "10px" }}
          >
            <Langs str="YES" />
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
